import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  X,
  Check,
  User,
  FileText,
  Calendar,
  Paperclip,
  Edit3,
  Plus,
  History,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  Building2,
  Send,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import './PatientDetail.css';
import AppointmentModal from './AppointmentModal';


class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', background: '#ff000033', color: 'white', borderRadius: '8px', margin: '20px' }}>
          <h2>Algo salió mal al cargar esta sección.</h2>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>{this.state.error?.toString()}</pre>
          <button onClick={() => this.setState({hasError: false})} style={{ marginTop: '10px', padding: '5px 10px' }}>Reintentar</button>
        </div>
      );
    }
    return this.props.children;
  }
}


const PatientDetailInner = ({ patient, onClose }) => {
  // Load active staff
  const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
  const staffName = activeStaff.full_name || 'GDT Admin';
  const isAdmin = activeStaff?.type === 'Administrador' || activeStaff?.type === 'Secretaria/o' || staffName.toLowerCase().includes('administracion');

  const { addNotification } = useNotifications();
  const [currentPatient, setCurrentPatient] = useState(patient);
  const [activeTab, setActiveTab] = useState('evoluciones');
  const [isAppModalOpen, setIsAppModalOpen] = useState(false);
  const [appModalData, setAppModalData] = useState(null);
  const [evolutions, setEvolutions] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [professionals, setProfessionals] = useState([]);

  useEffect(() => {
    supabase.from('professionals').select('*').then(({data}) => {
       if (data) setProfessionals(data);
    });
  }, []);
  
  // New note creation state
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Edit states for Patient Datos Filiatorios
  const [isEditingDatos, setIsEditingDatos] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDni, setEditDni] = useState('');
  const [editBirthdate, setEditBirthdate] = useState('');
  const [editInsurance, setEditInsurance] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editAffiliateNumber, setEditAffiliateNumber] = useState('');
  const [editPatientType, setEditPatientType] = useState('Ambulatorio');
  const [editCity, setEditCity] = useState('');
  const [editProvince, setEditProvince] = useState('');
  const [editCountry, setEditCountry] = useState('Argentina');

  // Edit states for Medical Evolutions
  const [editingEvId, setEditingEvId] = useState(null);
  const [editingEvContent, setEditingEvContent] = useState('');
  const [savingEvChange, setSavingEvChange] = useState(false);

  useEffect(() => {
    if (patient) {
      setCurrentPatient(patient);
      setIsEditingDatos(false);
      setEditingEvId(null);
      fetchEvolutions(patient.id);
      fetchAppointments(patient.dni);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient]);

  useEffect(() => {
    // Notify AICopilot about current patient context
    if (currentPatient && evolutions) {
      window.dispatchEvent(new CustomEvent('ai_patient_context', {
        detail: { patient: currentPatient, evolutions }
      }));
    }
    return () => {
      window.dispatchEvent(new CustomEvent('ai_patient_context', { detail: null }));
    };
  }, [currentPatient, evolutions]);

  async function fetchEvolutions(patientId = currentPatient.id) {
    let query = supabase
      .from('medical_evolutions')
      .select('*')
      .eq('patient_id', patientId)
      .order('created_at', { ascending: false });

    if (staffName && staffName !== 'GDT Admin' && !isAdmin) {
      query = query.eq('professional_name', staffName);
    }

    const { data } = await query;
    if (data) setEvolutions(data);
  }

  async function fetchAppointments(patientDni = currentPatient.dni) {
    if (!patientDni) {
      setAppointments([]);
      return;
    }
    setLoading(true);
    let query = supabase
      .from('appointments')
      .select('*')
      .eq('patient_dni', String(patientDni).trim())
      .order('appointment_date', { ascending: false });

    if (staffName && staffName !== 'GDT Admin' && !isAdmin) {
      query = query.eq('professional_name', staffName);
    }

    const { data, error } = await query;
    if (error) console.error('fetchAppointments error:', error);
    if (data) setAppointments(data);
    setLoading(false);
  }

  const handleAddEvolution = async () => {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      const { error } = await supabase
        .from('medical_evolutions')
        .insert([{
          patient_id: currentPatient.id,
          content: newNote,
          professional_name: staffName,
          created_by_user: (() => { const u = JSON.parse(localStorage.getItem('gdt_active_user') || '{}'); const s = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}'); return u.display_name || u.username || s.full_name || 'Sistema'; })(),
          created_at: new Date().toISOString()
        }]);
      
      if (error) throw error;
      addNotification('Evolución guardada', 'success');
      setNewNote('');
      fetchEvolutions();
    } catch (err) {
      addNotification('Error al guardar: ' + err.message, 'error');
    } finally {
      setSavingNote(false);
    }
  };

  const handleNewAppointment = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setAppModalData({
      patient_id: currentPatient.id,
      patient: currentPatient.full_name,
      patient_dni: currentPatient.dni
    });
    setIsAppModalOpen(true);
  };

  const handleSaveAppointment = async (data) => {
    try {
      const { error } = await supabase.from('appointments').insert([{
        patient_id: data.patient_id || null,
        patient_dni: data.patient_dni || null,
        patient_name: data.patient,
        patient_email: data.patient_email || '',
        patient_phone: data.patient_phone || '',
        appointment_date: data.date,
        appointment_time: data.time,
        status: 'pendiente',
        admin_notes: data.notes,
        study_type: data.studyType || 'CONSULTA GENERAL',
        professional_name: data.professional,
        created_by_user: staffName || 'Sistema'
      }]);
      if (error) throw error;
      addNotification('Turno asignado correctamente', 'success');
      setIsAppModalOpen(false);
      fetchAppointments(currentPatient.dni);
    } catch (err) {
      addNotification('Error al asignar turno: ' + err.message, 'error');
    }
  };

  const handleStartEditingDatos = () => {
    setEditName(currentPatient.full_name || '');
    setEditDni(currentPatient.dni || '');
    setEditBirthdate(currentPatient.birthdate || '');
    setEditInsurance(currentPatient.insurance_provider || '');
    setEditPhone(currentPatient.phone || '');
    setEditEmail(currentPatient.email || '');
    setEditAddress(currentPatient.address || '');
    setEditAffiliateNumber(currentPatient.affiliate_number || '');
    setEditPatientType(currentPatient.patient_type || 'Ambulatorio');
    setEditCity(currentPatient.city || '');
    setEditProvince(currentPatient.province || '');
    setEditCountry(currentPatient.country || 'Argentina');
    setIsEditingDatos(true);
  };

  const handleSaveDatos = async () => {
    if (!editName.trim()) {
      addNotification('El Nombre Completo es obligatorio', 'error');
      return;
    }
    try {
      const { error } = await supabase
        .from('patients')
        .update({
          full_name: editName,
          dni: editDni || null,
          birthdate: editBirthdate || null,
          insurance_provider: editInsurance || null,
          phone: editPhone || null,
          email: editEmail || null,
          address: editAddress || null,
          affiliate_number: editAffiliateNumber || null,
          patient_type: editPatientType || 'Ambulatorio',
          city: editCity || null,
          province: editProvince || null,
          country: editCountry || 'Argentina'
        })
        .eq('id', currentPatient.id);

      if (error) throw error;

      const updatedPatient = {
        ...currentPatient,
        full_name: editName,
        dni: editDni,
        birthdate: editBirthdate,
        insurance_provider: editInsurance,
        phone: editPhone,
        email: editEmail,
        address: editAddress,
        affiliate_number: editAffiliateNumber,
        patient_type: editPatientType,
        city: editCity,
        province: editProvince,
        country: editCountry
      };
      
      setCurrentPatient(updatedPatient);
      setIsEditingDatos(false);
      addNotification('Datos filiatorios actualizados con éxito', 'success');
      
      fetchAppointments(editDni);
    } catch (err) {
      addNotification('Error al guardar cambios: ' + err.message, 'error');
    }
  };

  const handleStartEditingEv = (ev) => {
    setEditingEvId(ev.id);
    setEditingEvContent(ev.content || '');
  };

  const handleSaveEv = async (evId) => {
    if (!editingEvContent.trim()) {
      addNotification('La nota clínica no puede estar vacía', 'error');
      return;
    }
    setSavingEvChange(true);
    try {
      const { error } = await supabase
        .from('medical_evolutions')
        .update({ content: editingEvContent })
        .eq('id', evId);

      if (error) throw error;
      addNotification('Evolución clínica actualizada con éxito', 'success');
      setEditingEvId(null);
      fetchEvolutions();
    } catch (err) {
      addNotification('Error al actualizar evolución: ' + err.message, 'error');
    } finally {
      setSavingEvChange(false);
    }
  };

  if (!currentPatient) return null;

  const renderSidebarContent = () => {
    return (
      <div className="tab-pane">
        <div className="datos-pane-header" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
          {!isEditingDatos && (
            <button className="btn-toggle-edit" onClick={handleStartEditingDatos} style={{ fontSize: '0.75rem', padding: '0.4rem 0.8rem' }}>
              <Edit3 size={14} /> EDITAR
            </button>
          )}
        </div>

            {isEditingDatos ? (
              <div className="patient-edit-form-wrapper">
                <div className="detail-section card glass-panel editing">
                  <div className="section-header-wr">
                    <User size={16} /> <h3>Editar Ficha Identificatoria</h3>
                  </div>
                  <div className="detail-grid editing">
                    <div className="info-item-edit">
                      <label>Nombre Completo</label>
                      <input 
                        type="text" 
                        value={editName} 
                        onChange={(e) => setEditName(e.target.value)} 
                        placeholder="Ej: Juan Pérez"
                      />
                    </div>
                    <div className="info-item-edit">
                      <label>DNI / Documento</label>
                      <input 
                        type="text" 
                        value={editDni} 
                        onChange={(e) => setEditDni(e.target.value)} 
                        placeholder="Ej: 30123456"
                      />
                    </div>
                    <div className="info-item-edit">
                      <label>Fecha de Nacimiento</label>
                      <input 
                        type="date" 
                        value={editBirthdate} 
                        onChange={(e) => setEditBirthdate(e.target.value)} 
                      />
                    </div>
                    <div className="info-item-edit">
                      <label>Obra Social</label>
                      <input 
                        type="text" 
                        value={editInsurance} 
                        onChange={(e) => setEditInsurance(e.target.value)} 
                        placeholder="Ej: OSDE, PAMI..."
                      />
                    </div>
                    <div className="info-item-edit">
                      <label>Nº de Afiliado</label>
                      <input 
                        type="text" 
                        value={editAffiliateNumber} 
                        onChange={(e) => setEditAffiliateNumber(e.target.value)} 
                        placeholder="Ej: 123456789"
                      />
                    </div>
                    <div className="info-item-edit">
                      <label>Tipo de Paciente</label>
                      <select 
                        value={editPatientType} 
                        onChange={(e) => setEditPatientType(e.target.value)}
                        style={{ width: '100%', padding: '0.6rem', borderRadius: '8px', border: '1px solid var(--border-glass)', background: 'rgba(255,255,255,0.05)', color: 'white' }}
                      >
                        <option value="Ambulatorio">Ambulatorio</option>
                        <option value="Internado">Internado</option>
                        <option value="A Domicilio">A Domicilio</option>
                        <option value="Telemedicina">Telemedicina</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="detail-section card glass-panel editing" style={{ marginTop: '1.5rem' }}>
                  <div className="section-header-wr">
                    <Phone size={16} /> <h3>Editar Datos de Contacto</h3>
                  </div>
                  <div className="detail-grid editing">
                    <div className="info-item-edit">
                      <label>Celular / WhatsApp</label>
                      <input 
                        type="text" 
                        value={editPhone} 
                        onChange={(e) => setEditPhone(e.target.value)} 
                        placeholder="Ej: +54 9 11..."
                      />
                    </div>
                    <div className="info-item-edit">
                      <label>Email</label>
                      <input 
                        type="email" 
                        value={editEmail} 
                        onChange={(e) => setEditEmail(e.target.value)} 
                        placeholder="Ej: correo@paciente.com"
                      />
                    </div>
                    <div className="info-item-edit full">
                      <label>Dirección</label>
                      <input 
                        type="text" 
                        value={editAddress} 
                        onChange={(e) => setEditAddress(e.target.value)} 
                        placeholder="Dirección, piso, depto..."
                      />
                    </div>
                    <div className="info-item-edit">
                      <label>Ciudad</label>
                      <input 
                        type="text" 
                        value={editCity} 
                        onChange={(e) => setEditCity(e.target.value)} 
                        placeholder="Ej: CABA"
                      />
                    </div>
                    <div className="info-item-edit">
                      <label>Provincia</label>
                      <input 
                        type="text" 
                        value={editProvince} 
                        onChange={(e) => setEditProvince(e.target.value)} 
                        placeholder="Ej: Buenos Aires"
                      />
                    </div>
                    <div className="info-item-edit">
                      <label>País</label>
                      <input 
                        type="text" 
                        value={editCountry} 
                        onChange={(e) => setEditCountry(e.target.value)} 
                        placeholder="Ej: Argentina"
                      />
                    </div>
                  </div>
                </div>

                <div className="edit-actions-bar">
                  <button className="btn-cancel-edit" onClick={() => setIsEditingDatos(false)}>
                    CANCELAR
                  </button>
                  <button className="btn-save-edit" onClick={handleSaveDatos}>
                    <Check size={16} /> GUARDAR CAMBIOS
                  </button>
                </div>
              </div>
            ) : (
              <div className="patient-read-only-wrapper">
                <div className="detail-section card glass-panel">
                  <div className="section-header-wr">
                    <User size={16} /> <h3>Ficha Identificatoria</h3>
                  </div>
                  <div className="detail-grid">
                    <div className="info-item">
                      <label>Nombre Completo</label>
                      <div className="value">{currentPatient.full_name}</div>
                    </div>
                    <div className="info-item">
                      <label>DNI / Documento</label>
                      <div className="value">{currentPatient.dni || '---'}</div>
                    </div>
                    <div className="info-item">
                      <label>Fecha de Nacimiento</label>
                      <div className="value">
                        {(() => {
                          if (!currentPatient.birthdate) return 'No especificada';
                          const d = new Date(currentPatient.birthdate + 'T00:00:00');
                          return isNaN(d) ? currentPatient.birthdate : format(d, 'dd/MM/yyyy');
                        })()}
                      </div>
                    </div>
                    <div className="info-item">
                      <label>Obra Social</label>
                      <div className="value">{currentPatient.insurance_provider || 'Particular'}</div>
                    </div>
                    <div className="info-item">
                      <label>Nº de Afiliado</label>
                      <div className="value">{currentPatient.affiliate_number || '---'}</div>
                    </div>
                    <div className="info-item">
                      <label>Tipo de Paciente</label>
                      <div className="value">
                        <span style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#60a5fa', padding: '2px 8px', borderRadius: '12px', fontSize: '0.8rem' }}>
                          {currentPatient.patient_type || 'Ambulatorio'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="detail-section card glass-panel" style={{ marginTop: '1.5rem' }}>
                  <div className="section-header-wr">
                    <Phone size={16} /> <h3>Contacto</h3>
                  </div>
                  <div className="detail-grid">
                    <div className="info-item">
                      <label>Celular / WhatsApp</label>
                      <div className="value">{currentPatient.phone || '---'}</div>
                    </div>
                    <div className="info-item">
                      <label>Email</label>
                      <div className="value">{currentPatient.email || '---'}</div>
                    </div>
                    <div className="info-item full">
                      <label>Dirección Física</label>
                      <div className="value">
                        {currentPatient.address 
                          ? `${currentPatient.address}${currentPatient.city ? `, ${currentPatient.city}` : ''}${currentPatient.province ? ` (${currentPatient.province})` : ''}` 
                          : 'Sin especificar'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
  };

  const renderMainContent = () => {
    switch (activeTab) {
      case 'evoluciones':
        return (
          <div className="tab-pane evolutions-pane">
            <div className="new-note-box card glass-panel">
              <textarea 
                placeholder="Escribir nueva evolución o nota clínica..." 
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
              <div className="note-actions">
                <button 
                  className="btn-save-note" 
                  disabled={savingNote || !newNote.trim()} 
                  onClick={handleAddEvolution}
                >
                  {savingNote ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                  REGISTRAR EVOLUCIÓN
                </button>
              </div>
            </div>

            <div className="timeline-v2">
              {evolutions.length === 0 ? (
                <div className="empty-timeline">No hay registros clínicos previos.</div>
              ) : evolutions.map(ev => (
                <div key={ev.id} className="timeline-item-v2">
                  <div className="item-marker"></div>
                  <div className="item-content card glass-panel">
                    <div className="item-header">
                      <span className="item-date">{format(new Date(ev.created_at), "d 'de' MMMM, yyyy", { locale: es })}</span>
                      <span className="item-prof">
                        {ev.professional_name}
                        {ev.created_by_user && (
                          <span style={{ fontSize: '0.8em', color: 'var(--accent)', marginLeft: '8px' }}>
                            (Op: {ev.created_by_user})
                          </span>
                        )}
                      </span>
                      
                      {editingEvId !== ev.id && (
                        <button className="btn-edit-ev-trigger" title="Editar evolución" onClick={() => handleStartEditingEv(ev)}>
                          <Edit3 size={14} />
                        </button>
                      )}
                    </div>
                    
                    {editingEvId === ev.id ? (
                      <div className="ev-edit-box">
                        <textarea 
                          value={editingEvContent}
                          onChange={(e) => setEditingEvContent(e.target.value)}
                          rows="4"
                        />
                        <div className="ev-edit-actions">
                          <button className="btn-cancel-ev-edit" disabled={savingEvChange} onClick={() => setEditingEvId(null)}>
                            CANCELAR
                          </button>
                          <button className="btn-save-ev-edit" disabled={savingEvChange || !editingEvContent.trim()} onClick={() => handleSaveEv(ev.id)}>
                            {savingEvChange ? <Loader2 className="animate-spin" size={14} /> : 'GUARDAR'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="item-text">{ev.content}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'turnos':
        return (
          <div className="tab-pane">
            <div className="section-header-wr">
              <Calendar size={16} /> <h3>Historial Completo de Turnos</h3>
            </div>
            <div className="table-container-wr">
              <table className="data-table-v2">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Hora</th>
                    <th>Profesional</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {appointments.length === 0 ? (
                    <tr><td colSpan="5" className="text-center-p">El paciente no registra turnos previos en el sistema.</td></tr>
                  ) : appointments.map(app => (
                    <tr key={app.id}>
                      <td>
                        {(() => {
                          if (!app.appointment_date) return '---';
                          const d = new Date(app.appointment_date + 'T00:00:00');
                          return isNaN(d) ? app.appointment_date : format(d, 'dd/MM/yyyy');
                        })()}
                      </td>
                      <td>{app.appointment_time?.substring(0, 5)} hs</td>
                      <td>{app.professional_name}</td>
                      <td>
                        <span className={`status-pill ${app.status}`}>{app.status.toUpperCase()}</span>
                      </td>
                      <td>
                        <button 
                          type="button" 
                          className="btn-toggle-edit" 
                          style={{ padding: '4px 10px', fontSize: '0.7rem', display: 'inline-flex', alignItems: 'center', gap: '4px', border: '1px solid rgba(0, 229, 255, 0.25)' }}
                          onClick={() => {
                            if (currentPatient.email) {
                              window.location.href = `mailto:${currentPatient.email}?subject=Consulta Turno - GDT`;
                            } else {
                              alert('El paciente no tiene un correo electrónico configurado.');
                            }
                          }}
                        >
                          <Mail size={12} />
                          CORREO
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'archivos':
        const patientFiles = (() => {
          const files = [];
          appointments.forEach(app => {
            const details = app.study_details || {};
            const attached = details.attached_files || [];
            attached.forEach(file => {
              files.push({
                ...file,
                appointmentDate: app.appointment_date,
                studyType: app.study_type,
                professionalName: app.professional_name
              });
            });
          });
          return files;
        })();
        return (
          <div className="tab-pane">
            <div className="section-header-wr">
              <Paperclip size={16} /> <h3>Documentación Adjunta</h3>
            </div>
            <div className="patient-files-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
              {patientFiles.length === 0 ? (
                <div className="empty-timeline" style={{ gridColumn: '1 / -1' }}>El paciente no registra archivos adjuntos.</div>
              ) : patientFiles.map((file, idx) => (
                <div key={idx} className="file-item-premium card glass-panel" style={{ padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: '12px', background: 'rgba(15, 23, 42, 0.25)' }}>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', overflow: 'hidden' }}>
                    <div className="file-icon orange" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '8px', background: 'rgba(230, 126, 34, 0.1)', color: '#e67e22', flexShrink: 0 }}>
                      <FileText size={18} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                      <span className="file-name" style={{ color: 'white', fontWeight: '600', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.name}>{file.name}</span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {file.study_name && file.study_name !== 'General' ? file.study_name : (file.studyType || 'Consulta')}
                      </span>
                      <span className="file-date">
                        Turno: {(() => {
                          if (!file.appointmentDate) return '---';
                          const d = new Date(file.appointmentDate + 'T00:00:00');
                          return isNaN(d) ? file.appointmentDate : format(d, 'dd/MM/yyyy');
                        })()}
                      </span>
                    </div>
                  </div>
                  {file.url && (
                    <a href={file.url} target="_blank" rel="noreferrer" className="btn-view-file" style={{ background: 'rgba(0, 229, 255, 0.1)', color: '#00e5ff', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold', textDecoration: 'none', cursor: 'pointer', flexShrink: 0 }}>
                      VER
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div 
      className="patient-detail-overlay" 
      onClick={onClose}
      style={{ 
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
        zIndex: 99999, 
        backgroundColor: 'rgba(11, 11, 20, 0.85)',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        backdropFilter: 'blur(12px)'
      }}
    >
      <div className="patient-detail-container glass-modal-premium split-layout" onClick={e => e.stopPropagation()} style={{ zIndex: 100000, position: 'relative' }}>
        
        <div className="pd-sidebar">
          <div className="pd-sidebar-header">
            <button className="btn-close-p" onClick={onClose}><X size={20} /></button>
            <div className="p-avatar-lg">{currentPatient.full_name ? currentPatient.full_name[0].toUpperCase() : 'P'}</div>
            <div className="p-name-meta" style={{ textAlign: 'center' }}>
              <h2>{currentPatient.full_name}</h2>
              <div className="p-meta-tags" style={{ justifyContent: 'center' }}>
                <span>DNI: {currentPatient.dni || '---'}</span>
                <span className="dot">•</span>
                <span>{currentPatient.insurance_provider || 'Particular'}</span>
              </div>
            </div>
          </div>
          <div style={{ padding: '0 2rem 1.5rem 2rem' }}>
              <button className="btn-agendar-turno-sidebar" onClick={handleNewAppointment} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', background: 'var(--primary-cyan)', color: '#0f172a', border: 'none', borderRadius: '12px', fontWeight: '800', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 15px rgba(0, 191, 165, 0.3)' }}>
                <Calendar size={18} /> AGENDAR TURNO
              </button>
            </div>
          <div className="pd-sidebar-content">
            {renderSidebarContent()}
          </div>
        </div>

        <div className="pd-main">
          <div className="detail-tabs-premium">
            <button className={`tab-btn-p ${activeTab === 'evoluciones' ? 'active' : ''}`} onClick={() => setActiveTab('evoluciones')}>
              <History size={18} /> HISTORIA CLÍNICA
            </button>
            <button className={`tab-btn-p ${activeTab === 'turnos' ? 'active' : ''}`} onClick={() => setActiveTab('turnos')}>
              <Calendar size={18} /> TURNOS
            </button>
            <button className={`tab-btn-p ${activeTab === 'archivos' ? 'active' : ''}`} onClick={() => setActiveTab('archivos')}>
              <Paperclip size={18} /> ARCHIVOS
            </button>
          </div>

          <div className="detail-body-premium">
            {renderMainContent()}
          </div>
        </div>
      </div>
      <AppointmentModal isOpen={isAppModalOpen} onClose={() => setIsAppModalOpen(false)} initialData={appModalData} onSave={handleSaveAppointment} professionalsList={professionals} />
    </div>
  );
};


const PatientDetail = (props) => (
  <ErrorBoundary>
    <PatientDetailInner {...props} />
  </ErrorBoundary>
);

export default PatientDetail;
